import { Controller } from "@hotwired/stimulus"

/**
 * Employee Hours Controller
 * Calculates employee block duration: (to - from - break) for time_range mode
 * Supports employee count multiplication and total hours display
 */
export default class extends Controller {
  static targets = ["entries", "total"]

  connect() {
    requestAnimationFrame(() => this.calculate())

    this.element.addEventListener("change", this.handleChange.bind(this))
    this.element.addEventListener("input", this.handleChange.bind(this))

    // Observe DOM for new rows being added
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
          requestAnimationFrame(() => this.calculate())
          break
        }
      }
    })

    const entriesContainer = this.hasEntriesTarget ? this.entriesTarget : this.element
    this.observer.observe(entriesContainer, { childList: true, subtree: true })
  }

  disconnect() {
    this.element.removeEventListener("change", this.handleChange.bind(this))
    this.element.removeEventListener("input", this.handleChange.bind(this))
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  handleChange() {
    this.calculate()
  }

  calculate() {
    let totalHours = 0

    const rows = this.element.querySelectorAll('.nested-fields:not([style*="display: none"])')

    rows.forEach((row) => {
      const destroyField = row.querySelector('input[name*="_destroy"]')
      if (destroyField && (destroyField.value === "true" || destroyField.value === "1")) return

      const fromInput = row.querySelector('[data-employee-hours-target="from"]')
      const toInput = row.querySelector('[data-employee-hours-target="to"]')
      const breakInput = row.querySelector('[data-employee-hours-target="break"]')
      const hoursInput = row.querySelector('[data-employee-hours-target="hours"]')
      const manualHoursInput = row.querySelector('[data-employee-hours-target="manualHours"]')
      const employeeCountInput = row.querySelector('[data-employee-hours-target="employeeCount"]')
      const employeeSelect = row.querySelector('[data-employee-hours-target="employeeIds"]')
      const multiplyHoursInput = row.querySelector('[data-employee-hours-target="multiplyHours"]')

      // Time-range mode: calculate from/to/break
      if (fromInput && toInput && hoursInput) {
        const fromTime = this.parseTime(fromInput.value)
        const toTime = this.parseTime(toInput.value)
        const breakTime = breakInput ? this.parseTime(breakInput.value) : 0

        if (fromTime === null || toTime === null) {
          hoursInput.value = ""
          return
        }

        let duration = (toTime - fromTime) / 60
        if (duration < 0) duration += 24

        if (breakTime !== null && breakTime > 0) {
          duration -= breakTime / 60
        }

        // Multiply by employee count or selected employees if setting enabled
        const shouldMultiply = multiplyHoursInput && multiplyHoursInput.value === "1"
        if (shouldMultiply) {
          if (employeeCountInput && employeeCountInput.value) {
            const count = parseInt(employeeCountInput.value, 10)
            if (count > 0) duration *= count
          } else if (employeeSelect) {
            let empCount = 0
            if (employeeSelect.selectedOptions) {
              empCount = Array.from(employeeSelect.selectedOptions).filter(
                (opt) => opt.value !== "",
              ).length
            }
            if (empCount > 0) duration *= empCount
          }
        }

        duration = Math.round(duration * 100) / 100

        if (duration > 0) {
          hoursInput.value = duration.toFixed(2)
          totalHours += duration
        } else {
          hoursInput.value = ""
        }
      } else if (manualHoursInput) {
        // Manual mode: just sum up manually entered hours
        const hours = parseFloat(manualHoursInput.value) || 0
        if (hours > 0) {
          const shouldMultiply = multiplyHoursInput && multiplyHoursInput.value === "1"
          let effectiveHours = hours

          if (shouldMultiply) {
            if (employeeCountInput && employeeCountInput.value) {
              const count = parseInt(employeeCountInput.value, 10)
              if (count > 0) effectiveHours = hours * count
            } else if (employeeSelect) {
              let empCount = 0
              if (employeeSelect.selectedOptions) {
                empCount = Array.from(employeeSelect.selectedOptions).filter(
                  (opt) => opt.value !== "",
                ).length
              }
              if (empCount > 0) effectiveHours = hours * empCount
            }
          }

          totalHours += effectiveHours
        }
      }
    })

    if (this.hasTotalTarget) {
      this.totalTarget.textContent = totalHours.toFixed(2)
    }
  }

  /**
   * Parse time string (HH:MM) to minutes since midnight
   */
  parseTime(timeString) {
    if (!timeString) return null

    const parts = timeString.split(":")
    if (parts.length < 2) return null

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)

    if (isNaN(hours) || isNaN(minutes)) return null

    return hours * 60 + minutes
  }
}
